import { useAuth } from '../hooks/useAuth';
import { useQuery } from '@tanstack/react-query';
import { customerApi } from '@vmgate/common';
import { LoadingScreen } from '../components/common/LoadingScreen';
import { ErrorState } from '../components/common/ErrorState';
import { SummaryCard } from '../components/common/SummaryCard';
import { StatusBadge } from '../components/common/StatusBadge';
import { formatDateTime, formatNumber, formatRelative } from '../utils/format';

export function OverviewPage() {
  const { account, profile } = useAuth();

  const customerId = account?.customer_id;

  const licensesQuery = useQuery({
    queryKey: ['customer-licenses', customerId],
    queryFn: () => customerApi.getCustomerLicenses(customerId!),
    enabled: Boolean(customerId)
  });

  const devicesQuery = useQuery({
    queryKey: ['customer-devices', customerId],
    queryFn: () => customerApi.getCustomerDevices(customerId!),
    enabled: Boolean(customerId)
  });

  if (!account) {
    return <LoadingScreen />;
  }

  if (licensesQuery.isLoading || devicesQuery.isLoading) {
    return <LoadingScreen message="Preparing your dashboard..." />;
  }

  if (licensesQuery.isError || devicesQuery.isError || !licensesQuery.data || !devicesQuery.data) {
    return <ErrorState message="Unable to load customer dashboard." onRetry={() => { licensesQuery.refetch(); devicesQuery.refetch(); }} />;
  }

  const licenseData = licensesQuery.data;
  const deviceData = devicesQuery.data;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div className="grid-two">
        <SummaryCard
          label="Total licenses"
          value={formatNumber(licenseData.license_quota_total)}
          subtitle={`${formatNumber(licenseData.license_quota_assigned)} in use`}
          icon="🔐"
        />
        <SummaryCard
          label="Available"
          value={formatNumber(licenseData.license_quota_available)}
          subtitle="Remaining capacity"
          icon="📦"
        />
        <SummaryCard label="Devices registered" value={formatNumber(deviceData.devices.length)} icon="📱" />
        <SummaryCard
          label="Last account update"
          value={formatRelative(profile?.updated_at)}
          subtitle={formatDateTime(profile?.updated_at) || '—'}
          icon="🕒"
        />
      </div>

      <section className="surface" style={{ display: 'grid', gap: '1rem' }}>
        <header>
          <h2 style={{ margin: 0 }}>Latest licenses</h2>
          <p className="text-muted" style={{ margin: 0 }}>
            Recently issued activation certificates.
          </p>
        </header>

        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>License ID</th>
                <th>Status</th>
                <th>Issued</th>
                <th>Device</th>
                <th>Email</th>
              </tr>
            </thead>
            <tbody>
              {licenseData.licenses.slice(0, 5).map((license) => (
                <tr key={license.license_id}>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{license.license_id}</td>
                  <td>
                    <StatusBadge
                      label={license.status}
                      tone={license.status === 'ACTIVE' ? 'success' : license.status === 'REVOKED' ? 'danger' : 'info'}
                    />
                  </td>
                  <td>{formatDateTime(license.created_at)}</td>
                  <td>
                    {license.device_info ? (
                      <>
                        <div>{license.device_info.build_brand ?? 'Device'}</div>
                        <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                          {license.device_info.build_model ?? ''}
                        </div>
                      </>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td>{license.certificate_email || '—'}</td>
                </tr>
              ))}
              {licenseData.licenses.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted" style={{ textAlign: 'center', padding: '1rem' }}>
                    No licenses issued yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="surface" style={{ display: 'grid', gap: '1rem' }}>
        <header>
          <h2 style={{ margin: 0 }}>Registered devices</h2>
          <p className="text-muted" style={{ margin: 0 }}>
            Devices currently holding VMGate certificates.
          </p>
        </header>

        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>Device</th>
                <th>Android ID</th>
                <th>Status</th>
                <th>Registered</th>
              </tr>
            </thead>
            <tbody>
              {deviceData.devices.slice(0, 5).map((device) => (
                <tr key={device.device_id}>
                  <td>
                    <div>{device.build_brand ?? 'Device'}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      {device.build_model ?? ''}
                    </div>
                  </td>
                  <td style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{device.android_id}</td>
                  <td>
                    <StatusBadge
                      label={device.status}
                      tone={device.status === 'LICENSED' ? 'success' : device.status === 'REVOKED' ? 'danger' : 'info'}
                    />
                  </td>
                  <td>{formatDateTime(device.registered_at)}</td>
                </tr>
              ))}
              {deviceData.devices.length === 0 && (
                <tr>
                  <td colSpan={4} className="text-muted" style={{ textAlign: 'center', padding: '1rem' }}>
                    No devices registered yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

